// Per-company + per-period section layout store.
// Controls which sections are hidden and the order they appear in.
// Each month has its own layout so admins can customize the report
// independently for every publishing period.
//
// When Supabase is wired up, replace the storage layer with a table
// `investor_section_layout (company_id, period, hidden jsonb, order jsonb)`
// and keep the same public API.

const STORAGE_PREFIX = 'finkloz_section_layout_';
const CHANGE_EVENT = 'finkloz:section-layout-changed';

function key(companyId, period) {
  return `${STORAGE_PREFIX}${companyId || 'default'}_${period || 'default'}`;
}

const EMPTY = { hidden: [], order: [] };

export function getSectionLayout(companyId, period) {
  try {
    const raw = localStorage.getItem(key(companyId, period));
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
        order: Array.isArray(parsed.order) ? parsed.order : [],
      };
    }
  } catch { /* ignore */ }
  return EMPTY;
}

export function saveSectionLayout(companyId, period, layout) {
  try {
    localStorage.setItem(key(companyId, period), JSON.stringify(layout));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('saveSectionLayout failed', e);
  }
}

export function hideSection(companyId, period, sectionId) {
  const layout = getSectionLayout(companyId, period);
  if (!layout.hidden.includes(sectionId)) {
    saveSectionLayout(companyId, period, { ...layout, hidden: [...layout.hidden, sectionId] });
  }
}

export function showSection(companyId, period, sectionId) {
  const layout = getSectionLayout(companyId, period);
  saveSectionLayout(companyId, period, { ...layout, hidden: layout.hidden.filter(id => id !== sectionId) });
}

export function moveSection(companyId, period, allSectionIds, sectionId, direction) {
  const layout = getSectionLayout(companyId, period);
  // Bootstrap order from all known ids if empty
  const current = layout.order.length ? [...layout.order] : [...allSectionIds];
  // Ensure unknown ids appended at the end
  for (const id of allSectionIds) if (!current.includes(id)) current.push(id);
  const idx = current.indexOf(sectionId);
  if (idx < 0) return;
  const target = idx + direction;
  if (target < 0 || target >= current.length) return;
  [current[idx], current[target]] = [current[target], current[idx]];
  saveSectionLayout(companyId, period, { ...layout, order: current });
}

// Return section ids in the correct order, filtering out hidden ones.
// `all` is the canonical list of section ids in their default order.
export function resolveOrder(layout, all) {
  if (!layout.order.length) return all.filter(id => !layout.hidden.includes(id));
  const set = new Set(layout.order);
  const ordered = [...layout.order.filter(id => all.includes(id))];
  // Append anything not in the saved order (new sections added later)
  for (const id of all) if (!set.has(id)) ordered.push(id);
  return ordered.filter(id => !layout.hidden.includes(id));
}

export function subscribeToSectionLayoutChanges(callback) {
  const handler = () => callback();
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}
